import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { clearForecastsQueryCache } from '@futrli/views/queries/forecasts';
import { getPredictForecastSalesUrl } from '@futrli/shared-services';
import { LoadingWizardScreen } from '@futrli/ui-components/components/sidebar/wizard/screens/loading-wizard-screen/loading-wizard-screen';

import { ForecastType as ForecastTypeType } from '../../../dashboard/types';
import { ThreeWayForecastCreatedWizardScreen } from './screens/three-way-forecast-created-wizard-screen';
import { ThreeWayForecastCreatingWizardScreen } from './screens/three-way-forecast-creating-wizard-screen';
import { ThreeWayForecastFormWizardScreen } from './screens/three-way-forecast-form-wizard-screen';
import { trackCreateForecastEvent } from '../../../dashboard/utils';
import { useCreateForecastWizardContext } from '../../contexts/create-forecast-wizard-context';
import { useCreateOrUploadForecast } from '../../hooks/useCreateOrUploadForecast';
import { useOrgRouteParams } from '../../../../hooks/useOrgRouteParams/useOrgRouteParams';

export interface CreateThreeWayForecastWizardContainerProps {
  onClose: () => void;
}

export const CreateThreeWayForecastWizardContainer = ({ onClose }: CreateThreeWayForecastWizardContainerProps) => {
  const navigate = useNavigate();

  const { workspaceId, organisationId } = useOrgRouteParams();
  const { form, excelField, isExcelUpload, view } = useCreateForecastWizardContext();
  const { errorCreating, isLoading, isCreating, newForecastId: newStandardForecastId } = view.state;

  const { getExcelRequestData, getRequestData, validate } = form;
  const { file: excelFile } = excelField;

  const {
    createForecastFromExcelFile,
    error: excelError,
    isSaving: isSavingExcel,
    newForecastId: newExcelForecastId
  } = useCreateOrUploadForecast(workspaceId, organisationId);

  const error = errorCreating ? true : excelError;
  const newForecastId = newStandardForecastId || newExcelForecastId;

  const screen = (() => {
    if (isLoading) {
      return 'loading';
    }
    if (isCreating || isSavingExcel) {
      return 'creating';
    }
    if (newForecastId) {
      return 'created';
    }
    return 'form';
  })();

  const handleCreate = () => {
    if (validate()) {
      if (isExcelUpload && excelFile) {
        createForecastFromExcelFile(excelFile, getExcelRequestData('scenario'));
      } else {
        view.createForecast(getRequestData());
      }
    }
  };

  const handleView = () => {
    if (newForecastId) {
      trackCreateForecastEvent(
        form.type === 'fixed' ? ForecastTypeType.SCENARIO : ForecastTypeType.SCENARIO_WITH_INVOICES
      );
      onClose();
      navigate(getPredictForecastSalesUrl(workspaceId, organisationId, newForecastId));
    }
  };

  // Refresh forecasts cache if a new forecast has been created
  useEffect(() => {
    if (newExcelForecastId) {
      clearForecastsQueryCache(organisationId, workspaceId);
    }
  }, [newExcelForecastId, organisationId, workspaceId]);

  return (
    <>
      {screen === 'loading' && <LoadingWizardScreen />}
      {screen === 'form' && (
        <ThreeWayForecastFormWizardScreen error={error} onClose={onClose} onCreate={handleCreate} />
      )}
      {screen === 'creating' && <ThreeWayForecastCreatingWizardScreen />}
      {screen === 'created' && <ThreeWayForecastCreatedWizardScreen onClose={onClose} onView={handleView} />}
    </>
  );
};
